import { type RefObject, useLayoutEffect, useState } from "react";
import { freeWidthInRow } from "./selectors/fit-label";
import { px, screenScale } from "./selectors/measure-free-width";

// The row's width and its other children's must not depend on the item holding
// `selfRef`, or each resize would feed back into the next measure.
export function useFlexRowFreeWidth<T extends HTMLElement = HTMLDivElement>({
  max,
  selfRef,
}: {
  max: number;
  selfRef: RefObject<HTMLElement | null>;
}) {
  const [row, rowRef] = useState<T | null>(null);
  const [freeWidth, setFreeWidth] = useState<number>();

  useLayoutEffect(() => {
    const self = selfRef.current;
    if (!row || !self) return;
    const others = [...row.children].filter(
      (child): child is HTMLElement =>
        child instanceof HTMLElement && !child.contains(self)
    );

    const measure = () => {
      const style = getComputedStyle(row);
      const content =
        row.clientWidth - px(style.paddingLeft) - px(style.paddingRight);
      if (!(content > 0)) return;
      const scale = screenScale(row);
      const widths = others.map(
        (child) => child.getBoundingClientRect().width / scale
      );
      const free = Math.floor(
        freeWidthInRow(content, px(style.columnGap), widths)
      );
      setFreeWidth(Math.min(max, free));
    };
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(row);
    for (const child of others) observer.observe(child);
    return () => observer.disconnect();
  }, [row, max, selfRef]);

  return { rowRef, freeWidth };
}
